import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import api from '../services/api';

// Capture recovery indicators from URL hash BEFORE Supabase strips them (runs at import time)
const initialHash = window.location.hash;
const hasRecoveryInUrl =
  initialHash.includes('type=recovery') ||
  new URLSearchParams(window.location.search).get('type') === 'recovery';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const { updatePassword, isRecoverySession, signOut } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [checkingSession, setCheckingSession] = useState(true);
  const [validRecovery, setValidRecovery] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Already confirmed via PASSWORD_RECOVERY event or sessionStorage
    if (isRecoverySession) {
      setValidRecovery(true);
      setCheckingSession(false);
      return;
    }

    // URL had recovery indicators — wait for Supabase to exchange the token
    if (hasRecoveryInUrl) {
      const timeout = setTimeout(async () => {
        // Check if Supabase created a session from the recovery token
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setValidRecovery(true);
        } else {
          setErrorMessage('This password reset link is invalid or expired. Please request a new one.');
        }
        setCheckingSession(false);
      }, 3000);

      return () => clearTimeout(timeout);
    }

    // No recovery signal at all — direct navigation by a regular user
    setErrorMessage('This password reset link is invalid or expired. Please request a new one.');
    setCheckingSession(false);
  }, [isRecoverySession]);

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters.';
    }

    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password)) {
      return 'Use at least one uppercase letter, one lowercase letter, and one number.';
    }

    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const validationError = validatePassword();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await updatePassword(password);
      if (error) {
        setErrorMessage(error.message || 'Failed to update password.');
        return;
      }

      // ── Keep OUR record in step with the provider's ─────────────────────
      // Supabase now has the new password, but users.password_hash did not
      // change — this page only ever called updateUser. The two then disagreed
      // about the same account: the customer could sign in with the password
      // they had just set, while `has_password` stayed false, so Settings kept
      // offering to "Set a Password" and never asked for the current one.
      //
      // Most visible on a Google account, which is what the report was about,
      // but it affected every reset: an email account that reset its password
      // had the same stale column.
      //
      // The email comes from the RECOVERY SESSION, never from the form. A typed
      // address here would be a way to set somebody else's password.
      //
      // Best-effort on purpose. The provider password has already changed by
      // this point, so the reset genuinely succeeded; reporting a failure now
      // would be a lie, and the worst case is that has_password stays stale
      // until the user sets one from Settings.
      try {
        const { data: { user: recoveredUser } } = await supabase.auth.getUser();
        if (recoveredUser?.email) {
          await api.setPassword(recoveredUser.email, password);
        }
      } catch (syncError) {
        // Never the password — only that the sync failed.
        console.error('Could not sync the new password to the account record.');
      }

      setSuccessMessage('Password updated successfully! Redirecting to sign in...');
      await signOut();
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1400);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to update password.');
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Set New Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter a new secure password for your account.
            </Typography>

            {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                sx={{ mb: 2 }}
                helperText="Min 8 chars with uppercase, lowercase, and number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type={showConfirm ? 'text' : 'password'}
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end">
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={submitting || !!successMessage || !validRecovery}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {submitting ? 'Updating...' : 'Update password'}
              </Button>
            </form>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button component={RouterLink} to="/forgot-password" variant="text">
                Request a New Reset Link
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default UpdatePassword;
